import { Phone, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import { Reveal, SectionHeading, GoldButton } from "@/components/Reveal";
import { IMAGES, PHONE, PHONE_LINK } from "@/data/site";

const NETWORK_COORDINATION = [
  "Like-for-like replacement vehicles",
  "Vehicle recovery",
  "Secure storage",
  "Insurance claims",
  "Personal injury referrals where appropriate",
  "Repair coordination when required",
];

const About = () => (
  <div data-testid="about-page">
    <PageHero
      testId="about-hero"
      kicker="About Sage Auto Group"
      titleLines={["Accident management,", "elevated to an art."]}
      description="At Sage Auto Group, we specialise in helping drivers after road traffic accidents. Our experienced team manages every stage of the process, so you never have to face it alone."
      image={IMAGES.about}
    />

    <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="about-story">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <SectionHeading
          kicker="Our Story"
          title="Stress-free by design."
          description="From the moment your accident happens until your claim is resolved, you have one dedicated point of contact. Our goal is simple: to remove the stress from the claims process while helping you achieve the best possible outcome."
        />
        <div>
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold" data-testid="about-network-kicker">
              Through our trusted nationwide network, we coordinate
            </p>
          </Reveal>
          <div className="mt-8 space-y-0 border-t border-white/10">
            {NETWORK_COORDINATION.map((item, i) => (
              <Reveal key={item} delay={i * 0.06}>
                <div className="flex items-center gap-4 border-b border-white/10 py-5" data-testid={`about-network-item-${i + 1}`}>
                  <Check className="h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                  <p className="font-serif text-xl text-white md:text-2xl">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-carbon py-28 md:py-36" data-testid="about-gallery">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <div className="group overflow-hidden rounded-2xl border border-white/10">
              <img
                src={IMAGES.interior}
                alt="Premium vehicle interior detail"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                data-testid="about-gallery-image-1"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="group h-full overflow-hidden rounded-2xl border border-white/10">
              <img
                src={IMAGES.rangeRover}
                alt="Black Range Rover on a dark street"
                className="aspect-[4/3] h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                data-testid="about-gallery-image-2"
              />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-16 max-w-3xl text-center font-serif text-2xl italic leading-snug text-white/80 md:text-3xl" data-testid="about-quote">
            "One Call. One Team. Complete Peace of Mind."
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee />

    <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="about-cta">
      <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-center">
        <SectionHeading
          kicker="Talk To Us"
          title="One call is all it takes."
        />
        <Reveal delay={0.15}>
          <GoldButton href={PHONE_LINK} testId="about-call-button">
            <Phone className="h-4 w-4" strokeWidth={2} /> Call {PHONE}
          </GoldButton>
        </Reveal>
      </div>
    </section>
  </div>
);

export default About;
